import { DiagnosticFormData } from '../types/diagnostic';

export interface ContextualRule {
  equipmentId: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
}

export function getContextualPriorities(formData: DiagnosticFormData): ContextualRule[] {
  const rules: ContextualRule[] = [];
  const isHighRisk = formData.crimeData && formData.crimeData.tauxPour1000Logements > 6.0;
  const isMediumRisk = formData.crimeData && formData.crimeData.tauxPour1000Logements > 4.0;

  // ══════════════════════════════════════════════════════════════
  // RÈGLES BASÉES SUR LE PROFIL D'OCCUPATION
  // ══════════════════════════════════════════════════════════════

  // Résidence secondaire + zone à risque → alarme télésurveillée HAUTE
  if (formData.occupationType === 'secondary_residence' && (isHighRisk || isMediumRisk)) {
    rules.push({
      equipmentId: 'remoteSurveillance',
      priority: 'high',
      reason: 'Résidence secondaire en zone à risque - surveillance 24/7 essentielle'
    });
    rules.push({
      equipmentId: 'alarm',
      priority: 'high',
      reason: 'Résidence secondaire nécessite une alarme'
    });
  }

  // Résidence secondaire (même sans risque élevé)
  if (formData.occupationType === 'secondary_residence') {
    rules.push({
      equipmentId: 'camera',
      priority: 'high',
      reason: 'Surveillance à distance pour résidence secondaire'
    });
    rules.push({
      equipmentId: 'lightTimer',
      priority: 'high',
      reason: 'Simulateur de présence pour résidence secondaire'
    });
  }

  // Famille avec enfants + RDC/étage bas → protection fenêtres HAUTE
  if (formData.householdComposition === 'family' && formData.floors <= 1) {
    rules.push({
      equipmentId: 'bars',
      priority: 'high',
      reason: 'Famille avec enfants au rez-de-chaussée - protection fenêtres prioritaire'
    });
    rules.push({
      equipmentId: 'glassBreakDetector',
      priority: 'high',
      reason: 'Détection bris de vitre pour sécurité famille'
    });
    rules.push({
      equipmentId: 'windowLocks',
      priority: 'high',
      reason: 'Sécurisation des fenêtres accessibles'
    });
  }

  // Absences prolongées fréquentes → simulateur + télésurveillance
  if (formData.presenceRhythm === 'frequent_absences') {
    rules.push({
      equipmentId: 'lightTimer',
      priority: 'high',
      reason: 'Absences prolongées - simulateur de présence indispensable'
    });
    rules.push({
      equipmentId: 'remoteSurveillance',
      priority: 'high',
      reason: 'Télésurveillance pour absences prolongées'
    });
    rules.push({
      equipmentId: 'camera',
      priority: 'high',
      reason: 'Surveillance à distance pendant vos absences'
    });
  }

  // Résidence secondaire / Occupation occasionnelle → télésurveillance obligatoire
  if (formData.presenceRhythm === 'occasional') {
    rules.push({
      equipmentId: 'remoteSurveillance',
      priority: 'high',
      reason: 'Résidence secondaire - télésurveillance 24/7 indispensable'
    });
    rules.push({
      equipmentId: 'camera',
      priority: 'high',
      reason: 'Surveillance permanente durant vos absences prolongées'
    });
    rules.push({
      equipmentId: 'lightTimer',
      priority: 'high',
      reason: 'Simulation de présence automatique'
    });
    rules.push({
      equipmentId: 'alarm',
      priority: 'high',
      reason: 'Alarme connectée avec notifications immédiates'
    });
  }

  // Absences régulières (travail) → dissuasion et détection
  if (formData.presenceRhythm === 'empty_daytime' || formData.presenceRhythm === 'mostly_occupied') {
    rules.push({
      equipmentId: 'securityStickers',
      priority: 'medium',
      reason: 'Absences régulières - dissuasion visuelle importante'
    });
    rules.push({
      equipmentId: 'lightTimer',
      priority: 'medium',
      reason: 'Simulation de présence durant les heures de travail'
    });
  }

  // Présence permanente → priorité aux équipements de base
  if (formData.presenceRhythm === 'permanent') {
    rules.push({
      equipmentId: 'securityStickers',
      priority: 'low',
      reason: 'Présence constante - dissuasion visuelle suffit'
    });
  }

  // ══════════════════════════════════════════════════════════════
  // RÈGLES BASÉES SUR L'ARCHITECTURE
  // ══════════════════════════════════════════════════════════════

  // Simple vitrage + RDC/étage bas → film anti-effraction HAUTE
  if (formData.windowType === 'single_glazing' && formData.floors <= 1) {
    rules.push({
      equipmentId: 'windowFilm',
      priority: 'high',
      reason: 'Simple vitrage très vulnérable au rez-de-chaussée'
    });
    rules.push({
      equipmentId: 'glassBreakDetector',
      priority: 'high',
      reason: 'Simple vitrage nécessite détection bris de vitre'
    });
  }

  // Simple vitrage (même en étage)
  if (formData.windowType === 'single_glazing') {
    rules.push({
      equipmentId: 'windowFilm',
      priority: 'medium',
      reason: 'Simple vitrage à sécuriser'
    });
  }

  // Pas de volets + zone peu éclairée → éclairage extérieur HAUTE
  if (formData.hasShutters === 'none' && formData.streetLighting === 'poor') {
    rules.push({
      equipmentId: 'lighting',
      priority: 'high',
      reason: 'Absence de volets en zone peu éclairée - éclairage automatique crucial'
    });
  }

  // Pas de volets → détecteurs bris de vitre
  if (formData.hasShutters === 'none') {
    rules.push({
      equipmentId: 'glassBreakDetector',
      priority: 'medium',
      reason: 'Sans volets, détection bris de vitre recommandée'
    });
  }

  // Volets présents mais pas de détecteurs → recommander détecteurs
  if (formData.hasShutters !== 'none' && !formData.equipment.includes('doorSensor')) {
    rules.push({
      equipmentId: 'doorSensor',
      priority: 'medium',
      reason: 'Vos volets constituent une première barrière - surveiller leur ouverture forcée'
    });
  }

  // Volets roulants motorisés → détecteurs encore plus importants
  if (formData.hasShutters === 'rolling' && !formData.equipment.includes('doorSensor')) {
    rules.push({
      equipmentId: 'doorSensor',
      priority: 'high',
      reason: 'Détecteurs essentiels pour volets roulants : soulèvement forcé détectable immédiatement'
    });
  }

  // Porte bois standard + zone à risque → porte blindée HAUTE
  if (formData.doorMaterial === 'standard_wood' && isHighRisk) {
    rules.push({
      equipmentId: 'door',
      priority: 'high',
      reason: 'Porte vulnérable en zone à risque élevé - blindage urgent'
    });
    rules.push({
      equipmentId: 'multipointLock',
      priority: 'high',
      reason: 'Renforcement serrure indispensable'
    });
    rules.push({
      equipmentId: 'doorHinges',
      priority: 'medium',
      reason: 'Renforcement gonds recommandé'
    });
  }

  // Porte bois standard (même sans risque élevé)
  if (formData.doorMaterial === 'standard_wood') {
    rules.push({
      equipmentId: 'multipointLock',
      priority: 'medium',
      reason: 'Porte standard à renforcer avec serrure multipoint'
    });
  }

  // Accès dissimulé + pas de portail → portail/caméras HAUTE
  if (formData.sideAccess === 'hidden_access') {
    if (!formData.equipment.includes('securityGate')) {
      rules.push({
        equipmentId: 'securityGate',
        priority: 'high',
        reason: 'Accès dissimulé non sécurisé - portail indispensable'
      });
    }
    if (!formData.equipment.includes('camera')) {
      rules.push({
        equipmentId: 'camera',
        priority: 'high',
        reason: 'Surveillance arrière/latérale essentielle pour accès dissimulé'
      });
    }
    rules.push({
      equipmentId: 'lighting',
      priority: 'high',
      reason: 'Éclairage périmètre pour accès dissimulé'
    });
    rules.push({
      equipmentId: 'motionSensor',
      priority: 'high',
      reason: 'Détection mouvement sur accès dissimulé'
    });
  }

  // Passage latéral accessible
  if (formData.sideAccess === 'side_passage') {
    rules.push({
      equipmentId: 'motionSensor',
      priority: 'medium',
      reason: 'Détection mouvement recommandée pour passage latéral'
    });
    rules.push({
      equipmentId: 'lighting',
      priority: 'medium',
      reason: 'Éclairage du passage latéral'
    });
  }

  // ══════════════════════════════════════════════════════════════
  // RÈGLES BASÉES SUR L'ENVIRONNEMENT ET LE RISQUE
  // ══════════════════════════════════════════════════════════════

  // Zone à risque élevé → équipements prioritaires
  if (isHighRisk) {
    if (!formData.equipment.includes('alarm')) {
      rules.push({
        equipmentId: 'alarm',
        priority: 'high',
        reason: 'Zone à risque élevé - alarme indispensable'
      });
    }
    if (!formData.equipment.includes('camera')) {
      rules.push({
        equipmentId: 'camera',
        priority: 'high',
        reason: 'Surveillance vidéo cruciale en zone à risque'
      });
    }
  }

  // Visibilité faible depuis la rue
  if (formData.streetVisibility === 'low') {
    rules.push({
      equipmentId: 'camera',
      priority: 'high',
      reason: 'Logement peu visible - surveillance vidéo importante'
    });
    rules.push({
      equipmentId: 'lighting',
      priority: 'high',
      reason: 'Logement en retrait nécessite éclairage renforcé'
    });
    rules.push({
      equipmentId: 'alarm',
      priority: 'high',
      reason: 'Faible visibilité augmente le besoin d\'alarme'
    });
  }

  // Quartier isolé
  if (formData.neighborhoodType === 'isolated') {
    rules.push({
      equipmentId: 'alarm',
      priority: 'high',
      reason: 'Quartier isolé - système d\'alarme essentiel'
    });
    rules.push({
      equipmentId: 'remoteSurveillance',
      priority: 'high',
      reason: 'Isolement nécessite télésurveillance'
    });
  }

  // Jardin accessible + pas de portail
  if (formData.hasAccessibleGarden && !formData.equipment.includes('securityGate')) {
    rules.push({
      equipmentId: 'securityGate',
      priority: 'high',
      reason: 'Jardin accessible non sécurisé - portail urgent'
    });
  }

  // Sous-sol présent
  if (formData.hasBasement) {
    rules.push({
      equipmentId: 'doorSensor',
      priority: 'medium',
      reason: 'Sous-sol à surveiller avec détecteurs d\'ouverture'
    });
    rules.push({
      equipmentId: 'motionSensor',
      priority: 'medium',
      reason: 'Détection mouvement recommandée au sous-sol'
    });
  }

  return rules;
}